import { isRecord } from "../document/model";
import type { SceneNode } from "../scene/model";
import { defaultQuad, frameWarp } from "./deformation";
import { hasEdgeWarp, validEdgeWarp, zeroEdges } from "../geometry/edge-warp";
import { legacyNumber, unsupported } from "./legacy-import-text";

export const legacyImageFields =
  "src crossOrigin cropX cropY filters resizeFilter imageCornerRadius imageBlurAmount imageHue imageSaturation imageBackgroundRemovalEnabled imageBackgroundRemovalColor imageBackgroundRemovalSensitivity imageBackgroundRemovalSoftness imageBackgroundRemovalMode imageEdgeStyle imageEdgeAmount warpCorners warpEdges";
export const isLegacyImage = (d: Record<string, unknown>) =>
  d.type === "image" || d.type === "warped-image";

/** Defaults written on every decorative object by the old editor are not active interactions. */
const inactiveDefaults: Record<string, unknown> = {
  statsSaveMode: "answerAndCorrectness",
  scoreValue: 1,
  hoverAnimation: "none",
  clickAnimation: "none",
  releaseAnimation: "none",
  dynamicTextType: "none",
  visibilityConditionType: "answered",
  imageBackgroundRemovalSensitivity: 28,
  imageBackgroundRemovalSoftness: 12,
  imageBackgroundRemovalMode: "all",
  drawingHardness: 100,
  polygonSides: 3,
  polygonInnerRatio: 0.45,
  imageEdgeStyle: "none",
  imageEdgeAmount: 35,
};
export function isInactiveLegacyDefault(key: string, value: unknown) {
  return (
    Object.hasOwn(inactiveDefaults, key) && inactiveDefaults[key] === value
  );
}

export function importImageEffects(
  node: SceneNode,
  d: Record<string, unknown>,
): SceneNode {
  const num = (key: string, fallback = 0) => legacyNumber(d, key, fallback);
  const sx = num("scaleX", 1),
    sy = num("scaleY", 1),
    w = num("width"),
    h = num("height");
  let blur = num("imageBlurAmount");
  if (d.resizeFilter != null)
    unsupported("фильтр изменения размера изображения");
  if (d.filters != null) {
    if (!Array.isArray(d.filters)) unsupported("фильтры изображения");
    for (const filter of d.filters) {
      if (
        !isRecord(filter) ||
        filter.type !== "Blur" ||
        d.type !== "warped-image" ||
        Object.keys(filter).some((k) => !["type", "blur"].includes(k))
      )
        unsupported("фильтр изображения");
      const amount = legacyNumber(filter, "blur");
      if (amount < 0 || amount > 1) unsupported("размытие изображения");
      // The old WarpedImage decoder extracts its legacy Blur filter once.
      if (!blur) blur = Math.round(Math.min(0.5, amount) * 200);
    }
  }
  const radius = num("imageCornerRadius"),
    edge = String(d.imageEdgeStyle || "none");
  if ((sx !== 1 || sy !== 1) && (blur || radius || edge !== "none"))
    unsupported("эффекты изображения с ненормализованным масштабом");
  node = {
    ...node,
    effects: {
      ...node.effects,
      hue: num("imageHue"),
      saturation: num("imageSaturation"),
      blur,
      background: {
        enabled: d.imageBackgroundRemovalEnabled === true,
        color: String(d.imageBackgroundRemovalColor || "").toLowerCase(),
        sensitivity: num("imageBackgroundRemovalSensitivity", 28),
        softness: num("imageBackgroundRemovalSoftness", 12),
        mode: String(d.imageBackgroundRemovalMode || "all"),
      },
      edge: {
        style: edge === "fade" ? "blur" : edge,
        amount: num("imageEdgeAmount", 35),
      },
    },
  };
  if (
    d.imageBackgroundRemovalEnabled != null &&
    typeof d.imageBackgroundRemovalEnabled !== "boolean"
  )
    unsupported("включение удаления фона");
  if (radius) node.geometry = { ...node.geometry, cornerRadius: radius };
  if (d.warpEdges != null && d.warpCorners == null)
    unsupported("изгиб без углов деформации");
  if (d.warpCorners != null) {
    if (
      d.type !== "warped-image" ||
      !Array.isArray(d.warpCorners) ||
      d.warpCorners.length !== 4 ||
      w <= 0 ||
      h <= 0
    )
      unsupported("углы деформации изображения");
    const corners = d.warpCorners.map((p) => {
      if (!isRecord(p)) unsupported("угол деформации");
      return {
        x: legacyNumber(p, "x", NaN) / w,
        y: legacyNumber(p, "y", NaN) / h,
      };
    });
    let edges = zeroEdges();
    if (d.warpEdges != null) {
      if (!Array.isArray(d.warpEdges) || d.warpEdges.length !== 4)
        unsupported("изгиб сторон изображения");
      edges = d.warpEdges.map((p) => {
        if (!isRecord(p)) unsupported("средняя точка деформации");
        return {
          x: legacyNumber(p, "x", NaN) / w,
          y: legacyNumber(p, "y", NaN) / h,
        };
      });
      if (!validEdgeWarp(corners, edges))
        unsupported("пересечение изгибов изображения");
    }
    const defaults = defaultQuad(),
      deformed =
        hasEdgeWarp(edges) ||
        corners.some(
          (p, i) =>
            Math.abs(p.x - defaults[i]!.x) > 1e-8 ||
            Math.abs(p.y - defaults[i]!.y) > 1e-8,
        );
    if (deformed) {
      if (radius && !hasEdgeWarp(edges))
        unsupported("совместную деформацию и скругление старого изображения");
      node.geometry = {
        ...node.geometry,
        warpCorners: corners,
        ...(hasEdgeWarp(edges) ? { warpEdges: edges } : {}),
        warpMapping: "bilinear",
        warpSourceSize: {
          width: node.geometry.width!,
          height: node.geometry.height!,
        },
      };
      // Ordinary selection uses the actual deformed frame, without baking the image.
      node = frameWarp(node);
    }
  }
  return node;
}
